import { useState, useCallback } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  useHover,
  useDismiss,
  useInteractions,
  autoUpdate,
  safePolygon,
} from '@floating-ui/react';
import { useAuthStore } from '@/stores/authStore';

// Types matching DeckProfile from UserCard.tsx
type MKeyType = 'temporary' | 'protocol' | 'official' | 'standard';
type UserStatus = 'online' | 'idle' | 'busy' | 'offline';

interface Skill {
  name: string;
  displayName: string;
  confidence: number;
}

interface SpecialBadge {
  id: string;
  name: string;
  icon: string;
}

export interface DeckProfile {
  id: string;
  username: string;
  displayName: string;
  level: number;
  mkeyType?: MKeyType;
  bio: string | null;
  tagline: string | null;
  avatarUrl: string | null;
  coverUrl: string | null;
  coverGradient?: string;
  storiesCount: number;
  connectionsCount: number;
  isCurrentUser: boolean;
  status?: UserStatus;
  isSubscriber?: boolean;
  primarySkill?: Skill | null;
  admirationsCount?: number;
  compatibilityScore?: number | null;
  compatiblePeopleCount?: number;
  specialBadges?: SpecialBadge[];
  hasFichaTecnica?: boolean;
  odysseyTitle?: string;
  login_streak?: number;
  subscriptionTier?: number; // 0=Free, 1-4=Tripulante, 5=Nobre
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Hook para buscar dados do UserCard
 * Reutilizado entre DECK e Chat hover
 */
export function useUserCard() {
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<DeckProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    strategy: 'absolute',
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 500, close: 150 },
    handleClose: safePolygon({
      buffer: 1,
    }),
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  /**
   * Fetch user card data from /deck/card/:userId endpoint
   */
  const fetchUserCard = useCallback(async (userId: string) => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    const token = useAuthStore.getState().token;

    try {
      const response = await fetch(`${API_BASE_URL}/deck/card/${userId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user card');
      }

      const data = await response.json();

      if (data.success && data.profile) {
        setProfile(data.profile);
      } else {
        // Fallback profile
        setProfile({
          id: userId,
          username: userId.slice(0, 8),
          displayName: 'Tripulante',
          level: 1,
          bio: null,
          tagline: null,
          avatarUrl: null,
          coverUrl: null,
          storiesCount: 0,
          connectionsCount: 0,
          isCurrentUser: false,
          status: 'offline',
          isSubscriber: false,
          primarySkill: null,
          admirationsCount: 0,
          compatibilityScore: null,
          compatiblePeopleCount: 0,
          specialBadges: [],
          hasFichaTecnica: false,
          odysseyTitle: 'Recruta',
        });
      }
    } catch (err: any) {
      console.error('[useUserCard] Error fetching card:', err);
      setError(err.message);
      // Set fallback profile on error
      setProfile({
        id: userId,
        username: userId.slice(0, 8),
        displayName: 'Tripulante',
        level: 1,
        bio: null,
        tagline: null,
        avatarUrl: null,
        coverUrl: null,
        storiesCount: 0,
        connectionsCount: 0,
        isCurrentUser: false,
        status: 'offline',
        isSubscriber: false,
        primarySkill: null,
        admirationsCount: 0,
        compatibilityScore: null,
        compatiblePeopleCount: 0,
        specialBadges: [],
        hasFichaTecnica: false,
        odysseyTitle: 'Recruta',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear current profile
   */
  const clearProfile = useCallback(() => {
    setProfile(null);
    setError(null);
  }, []);

  return {
    isOpen,
    profile,
    loading,
    error,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    fetchUserCard,
    clearProfile,
    setIsOpen,
  };
}
